// ResourcesSection.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ResourcesSection = () => {
  const [activeResource, setActiveResource] = useState(0);

  const resources = [
    {
      title: 'Study Abroad Pathways',
      description: 'Material covering the parts of an admissions cycle that people usually get wrong: reading entry requirements properly, writing a statement that answers the question asked, and keeping track of what has been sent where.\n\nThese sit in the client portal and open up once your consultation and onboarding are done.'
    },
    {
      title: 'Work Abroad Opportunities',
      description: 'Notes on what overseas employers tend to look for, how their interviews are usually run, and which documents you should have certified and ready before a role comes up rather than after.'
    },
    {
      title: 'Consultation & Eligibility Tools',
      description: 'Checklists that let you work out, on paper, whether a route is open to you and what it would take to qualify. The point is to find out before you commit money or time, not afterwards.'
    },
    {
      title: 'Preparation & Settlement Guides',
      description: 'Guidance for the months either side of the move itself: finding somewhere to live, registering with local services, and knowing how things like banking and healthcare tend to work where you are going.'
    }
  ];

  return (
    <section className="py-16 bg-primary">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Side - Value Statement */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm text-gray-300 font-light mb-3">What You Also Get</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                More Than Advice
              </h2>
              <p className="text-gray-300 text-sm font-light leading-relaxed mb-6">
                Advice on its own tends to evaporate the moment you sit down to fill in a form. Ivipass builds the reference material to go with it, so that the guidance you were given in a consultation is still there in writing when you need it three months later.
              </p>
              <p className="text-gray-300 text-sm font-light leading-relaxed mb-8">
                The material follows the same order as the process itself, from the first eligibility check through to settling in once you arrive.
              </p>
            </motion.div>
          </div>

          {/* Right Side - Interactive Panel */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-secondary rounded-lg overflow-hidden"
            >
              <div className="grid md:grid-cols-5">
                {/* Sidebar List */}
                <div className="md:col-span-2 bg-secondary-deep p-6">
                  {resources.map((resource, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveResource(index)}
                      className={`w-full text-left py-4 px-4 mb-2 rounded transition-colors ${
                        activeResource === index
                          ? 'bg-secondary text-white'
                          : 'text-gray-300 hover:bg-secondary/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-normal ${
                          activeResource === index ? 'bg-white text-secondary' : 'bg-secondary text-white'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="text-sm font-normal">{resource.title}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Main Content Panel */}
                <div className="md:col-span-3 p-8">
                  <motion.div
                    key={activeResource}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="text-2xl font-semibold text-white mb-6">
                      {resources[activeResource].title}
                    </h3>
                    <p className="text-gray-200 text-sm font-light leading-relaxed whitespace-pre-line">
                      {resources[activeResource].description}
                    </p>
                    <button className="mt-8 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition">
                      <ArrowRight size={20} className="text-secondary" />
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;
